import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { reportAPI } from '../services/api';
import { toast } from 'react-toastify';
import { datePickerStyles, datePickerPopperStyles } from '../styles/datePickerStyles';
import { darkSelectMenuProps } from '../styles/selectMenuStyles';

const ReportsPage = () => {
    const [reportType, setReportType] = useState('monthly');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [startDate, setStartDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(dayjs());
    const [courtFilter, setCourtFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [availableYears, setAvailableYears] = useState([]);
    const [availableMonths, setAvailableMonths] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const logoColors = {
        primary: '#3f51b5',    // Main blue
        secondary: '#c4d835'   // Bright yellow
    };
    // This page only tracks 2 colors of the shared palette - fill in the rest so the
    // dropdown menu styling below matches the dark theme used everywhere else.
    const selectColors = { darkBlue: '#2c387e', lightYellow: '#d4e157', brightYellow: logoColors.secondary };

    useEffect(() => {
        loadAvailableYears();
    }, []);

    useEffect(() => {
        if (selectedYear) {
            loadAvailableMonths(selectedYear);
        }
    }, [selectedYear]);

    const loadAvailableYears = async () => {
        try {
            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Timeout')), 10000)
            );
            const response = await Promise.race([
                reportAPI.getAvailableYears(),
                timeoutPromise
            ]);
            const years = response.data;
            
            // If no years in database, use current year and previous year
            if (years.length === 0) {
                const currentYear = new Date().getFullYear();
                setAvailableYears([currentYear - 1, currentYear]);
            } else {
                setAvailableYears(years);
            }
        } catch (error) {
            console.error('Failed to load years:', error);
            // Fallback to current year if API fails
            const currentYear = new Date().getFullYear();
            setAvailableYears([currentYear - 1, currentYear]);
        }
    };

    const loadAvailableMonths = async (year) => {
        try {
            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Timeout')), 10000)
            );
            const response = await Promise.race([
                reportAPI.getAvailableMonths(year),
                timeoutPromise
            ]);
            const months = response.data;
            
            // If no months in database for this year, use all months
            if (months.length === 0) {
                setAvailableMonths([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
            } else {
                setAvailableMonths(months);
            }
        } catch (error) {
            console.error('Failed to load months:', error);
            // Fallback to all months if API fails
            setAvailableMonths([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
        }
    };

    const generateReport = async () => {
        setLoading(true);
        try {
            let response;
            
            // Always use custom report with filters for consistency
            let startDateStr, endDateStr;
            
            switch (reportType) {
                case 'monthly':
                    startDateStr = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-01`;
                    endDateStr = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-${new Date(selectedYear, selectedMonth, 0).getDate()}`;
                    break;
                case 'weekly':
                    startDateStr = startDate.format('YYYY-MM-DD');
                    endDateStr = startDate.add(6, 'day').format('YYYY-MM-DD');
                    break;
                case 'custom':
                    startDateStr = startDate.format('YYYY-MM-DD');
                    endDateStr = endDate.format('YYYY-MM-DD');
                    break;
                default:
                    throw new Error('Invalid report type');
            }
            
            response = await reportAPI.getCustomReport(
                startDateStr, 
                endDateStr, 
                courtFilter || null, 
                statusFilter || null
            );
            
            setReportData(response.data);
            toast.success('Report generated successfully!');
        } catch (error) {
            console.error('Failed to generate report:', error);
            toast.error('Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    const exportReport = async () => {
        setExporting(true);
        try {
            let response;
            
            // Always use custom report with filters for consistency
            let startDateStr, endDateStr;
            
            switch (reportType) {
                case 'monthly':
                    startDateStr = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-01`;
                    endDateStr = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-${new Date(selectedYear, selectedMonth, 0).getDate()}`;
                    break;
                case 'weekly':
                    startDateStr = startDate.format('YYYY-MM-DD');
                    endDateStr = startDate.add(6, 'day').format('YYYY-MM-DD');
                    break;
                case 'custom':
                    startDateStr = startDate.format('YYYY-MM-DD');
                    endDateStr = endDate.format('YYYY-MM-DD');
                    break;
                default:
                    throw new Error('Invalid report type');
            }
            
            const reportRequest = {
                startDate: startDateStr,
                endDate: endDateStr,
                courtNumber: courtFilter || null,
                status: statusFilter || null
            };
            response = await reportAPI.exportReport(reportRequest);
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            let filename = 'booking-report.xlsx';
            if (reportType === 'monthly') {
                filename = `monthly-report-${selectedYear}-${selectedMonth.toString().padStart(2, '0')}.xlsx`;
            } else if (reportType === 'weekly') {
                filename = `weekly-report-${startDate.format('YYYY-MM-DD')}.xlsx`;
            } else {
                filename = `custom-report-${startDate.format('YYYY-MM-DD')}-to-${endDate.format('YYYY-MM-DD')}.xlsx`;
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success('Report exported successfully!');
        } catch (error) {
            console.error('Failed to export report:', error);
            toast.error('Failed to export report');
        } finally {
            setExporting(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-PK', {
            style: 'currency',
            currency: 'PKR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: 3 }}>
                <Paper sx={{ 
                    p: 3, 
                    mb: 3, 
                    backgroundColor: logoColors.primary,
                    borderRadius: 2
                }}>
                    <Typography variant="h4" sx={{ 
                        color: logoColors.secondary, 
                        mb: 3, 
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                        textAlign: 'center'
                    }}>
                        Booking Reports
                    </Typography>

                    <Typography variant="h6" sx={{ 
                        color: logoColors.secondary, 
                        mb: 2, 
                        fontWeight: 'bold',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                    }}>
                        Report Configuration
                    </Typography>
                    
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth sx={{ minWidth: '120px' }}>
                                <InputLabel sx={{ 
                                    color: logoColors.secondary, 
                                    fontWeight: 'bold',
                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                }}>Report Type</InputLabel>
                                <Select
                                    value={reportType}
                                    onChange={(e) => setReportType(e.target.value)}
                                    label="Report Type"
                                    sx={{ 
                                        '& .MuiSelect-select': { 
                                            color: logoColors.secondary,
                                            fontWeight: 'bold',
                                            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                            minWidth: '120px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'visible'
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: logoColors.secondary
                                        },
                                        '& .MuiSelect-root': {
                                            minWidth: '120px'
                                        }
                                    }}
                                    MenuProps={darkSelectMenuProps(selectColors)}
                                >
                                    <MenuItem value="monthly">Monthly Report</MenuItem>
                                    <MenuItem value="weekly">Weekly Report</MenuItem>
                                    <MenuItem value="custom">Custom Date Range</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {reportType === 'monthly' && (
                            <>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth sx={{ minWidth: '120px' }}>
                                        <InputLabel sx={{ 
                                    color: logoColors.secondary, 
                                    fontWeight: 'bold',
                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                }}>Year</InputLabel>
                                <Select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                    label="Year"
                                    sx={{ 
                                        '& .MuiSelect-select': { 
                                            color: logoColors.secondary,
                                            fontWeight: 'bold',
                                            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                            minWidth: '120px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'visible'
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: logoColors.secondary
                                        },
                                        '& .MuiSelect-root': {
                                            minWidth: '120px'
                                        }
                                    }}
                                    MenuProps={darkSelectMenuProps(selectColors)}
                                >
                                            {availableYears.length === 0 ? (
                                                <MenuItem value={new Date().getFullYear()}>
                                                    {new Date().getFullYear()} (No data yet)
                                                </MenuItem>
                                            ) : (
                                                availableYears.map(year => (
                                                    <MenuItem key={year} value={year}>{year}</MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth sx={{ minWidth: '120px' }}>
                                        <InputLabel sx={{ 
                                    color: logoColors.secondary, 
                                    fontWeight: 'bold',
                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                }}>Month</InputLabel>
                                <Select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    label="Month"
                                    sx={{ 
                                        '& .MuiSelect-select': { 
                                            color: logoColors.secondary,
                                            fontWeight: 'bold',
                                            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                            minWidth: '120px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'visible'
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: logoColors.secondary
                                        },
                                        '& .MuiSelect-root': {
                                            minWidth: '120px'
                                        }
                                    }}
                                    MenuProps={darkSelectMenuProps(selectColors)}
                                >
                                            {availableMonths.length === 0 ? (
                                                <MenuItem value={new Date().getMonth() + 1}>
                                                    {new Date().toLocaleString('default', { month: 'long' })} (No data yet)
                                                </MenuItem>
                                            ) : (
                                                availableMonths.map(month => (
                                                    <MenuItem key={month} value={month}>
                                                        {new Date(2024, month - 1).toLocaleString('default', { month: 'long' })}
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </>
                        )}

                        {reportType === 'weekly' && (
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    label="Start Date"
                                    value={startDate}
                                    onChange={(newValue) => setStartDate(newValue)}
                                    slotProps={{
                                        ...datePickerPopperStyles,
                                        textField: {
                                            InputProps: {
                                                sx: {
                                                    color: '#c4d835',
                                                    '& input': {
                                                        color: '#c4d835 !important',
                                                        WebkitTextFillColor: '#c4d835 !important'
                                                    }
                                                }
                                            },
                                            sx: datePickerStyles
                                        }
                                    }}
                                />
                            </Grid>
                        )}

                        {reportType === 'custom' && (
                            <>
                                <Grid item xs={12} md={3}>
                                    <DatePicker
                                        label="Start Date"
                                        value={startDate}
                                        onChange={(newValue) => setStartDate(newValue)}
                                        slotProps={{
                                            ...datePickerPopperStyles,
                                            textField: {
                                                InputProps: {
                                                    sx: {
                                                        color: '#c4d835',
                                                        '& input': {
                                                            color: '#c4d835 !important',
                                                            WebkitTextFillColor: '#c4d835 !important'
                                                        }
                                                    }
                                                },
                                                sx: datePickerStyles
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <DatePicker
                                        label="End Date"
                                        value={endDate}
                                        onChange={(newValue) => setEndDate(newValue)}
                                        slotProps={{
                                            ...datePickerPopperStyles,
                                            textField: {
                                                InputProps: {
                                                    sx: {
                                                        color: '#c4d835',
                                                        '& input': {
                                                            color: '#c4d835 !important',
                                                            WebkitTextFillColor: '#c4d835 !important'
                                                        }
                                                    }
                                                },
                                                sx: datePickerStyles
                                            }
                                        }}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth sx={{ minWidth: '120px' }}>
                                <Select
                                    value={courtFilter}
                                    onChange={(e) => setCourtFilter(e.target.value)}
                                    displayEmpty
                                    sx={{ 
                                        '& .MuiSelect-select': { 
                                            color: logoColors.secondary,
                                            fontWeight: 'bold',
                                            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                            minWidth: '120px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'visible'
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: logoColors.secondary
                                        },
                                        '& .MuiSelect-root': {
                                            minWidth: '120px'
                                        }
                                    }}
                                    MenuProps={darkSelectMenuProps(selectColors)}
                                >
                                    <MenuItem value="">All Courts</MenuItem>
                                    <MenuItem value="1">Court 1</MenuItem>
                                    <MenuItem value="2">Court 2</MenuItem>
                                    <MenuItem value="3">Court 3</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth sx={{ minWidth: '120px' }}>
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    displayEmpty
                                    sx={{ 
                                        '& .MuiSelect-select': { 
                                            color: logoColors.secondary,
                                            fontWeight: 'bold',
                                            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                            minWidth: '120px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'visible'
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: logoColors.secondary
                                        },
                                        '& .MuiSelect-root': {
                                            minWidth: '120px'
                                        }
                                    }}
                                    MenuProps={darkSelectMenuProps(selectColors)}
                                >
                                    <MenuItem value="">All Statuses</MenuItem>
                                    <MenuItem value="PENDING">Pending</MenuItem>
                                    <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                                    <MenuItem value="CONFLICT">Conflict</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={generateReport}
                            disabled={loading}
                            sx={{ 
                                backgroundColor: logoColors.secondary,
                                color: logoColors.primary,
                                fontWeight: 'bold',
                                textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                '&:hover': { 
                                    backgroundColor: logoColors.secondary,
                                    opacity: 0.9
                                }
                            }}
                        >
                            {loading ? <CircularProgress size={20} /> : 'Generate Report'}
                        </Button>
                        
                        <Button
                            variant="contained"
                            onClick={exportReport}
                            disabled={exporting || !reportData}
                            sx={{ 
                                backgroundColor: logoColors.primary,
                                color: logoColors.secondary,
                                fontWeight: 'bold',
                                textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                '&:hover': { 
                                    backgroundColor: logoColors.primary,
                                    opacity: 0.9
                                }
                            }}
                        >
                            {exporting ? <CircularProgress size={20} /> : 'Export to Excel'}
                        </Button>
                    </Box>
                </Paper>

                {reportData && reportData.totalBookings === 0 && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        No bookings found for the selected period. Try selecting a different date range or check if there are any bookings in the system.
                    </Alert>
                )}

                {reportData && reportData.totalBookings > 0 && (
                    <Grid container spacing={3}>
                        {/* Summary Cards */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ 
                                backgroundColor: logoColors.primary + '20',
                                border: `2px solid ${logoColors.primary}`,
                                borderRadius: 2
                            }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ 
                                        color: logoColors.secondary, 
                                        mb: 2, 
                                        fontWeight: 'bold',
                                        textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                    }}>
                                        Summary
                                    </Typography>
                                    <Typography variant="h4" sx={{ 
                                        color: logoColors.secondary, 
                                        fontWeight: 'bold',
                                        textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                    }}>
                                        {formatCurrency(reportData.totalRevenue)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                        color: logoColors.secondary,
                                        fontWeight: 'bold',
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                    }}>
                                        Total Revenue (Confirmed & Completed)
                                    </Typography>
                                    <Typography variant="h5" sx={{ 
                                        color: logoColors.secondary, 
                                        mt: 1,
                                        fontWeight: 'bold',
                                        textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                    }}>
                                        {reportData.totalBookings} Bookings
                                    </Typography>
                                    
                                    {/* Additional Amount Breakdown */}
                                    <Box sx={{ mt: 2, pt: 2, borderTop: `2px solid ${logoColors.primary}` }}>
                                        <Typography variant="body2" sx={{ 
                                            color: logoColors.secondary, 
                                            mb: 1, 
                                            fontWeight: 'bold',
                                            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                        }}>
                                            Other Amounts:
                                        </Typography>
                                        {reportData.totalPendingAmount > 0 && (
                                            <Typography variant="body2" sx={{ 
                                                color: logoColors.secondary,
                                                fontWeight: 'bold',
                                                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                            }}>
                                                Pending: {formatCurrency(reportData.totalPendingAmount)}
                                            </Typography>
                                        )}
                                        {reportData.totalCancelledAmount > 0 && (
                                            <Typography variant="body2" sx={{ 
                                                color: logoColors.secondary,
                                                fontWeight: 'bold',
                                                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                            }}>
                                                Cancelled: {formatCurrency(reportData.totalCancelledAmount)}
                                            </Typography>
                                        )}
                                        {reportData.totalConflictAmount > 0 && (
                                            <Typography variant="body2" sx={{
                                                color: logoColors.secondary,
                                                fontWeight: 'bold',
                                                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                            }}>
                                                Conflict: {formatCurrency(reportData.totalConflictAmount)}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Cash vs Online Breakdown */}
                                    <Box sx={{ mt: 2, pt: 2, borderTop: `2px solid ${logoColors.primary}` }}>
                                        <Typography variant="body2" sx={{
                                            color: logoColors.secondary,
                                            mb: 1,
                                            fontWeight: 'bold',
                                            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                        }}>
                                            Cash vs Online:
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: logoColors.secondary,
                                            fontWeight: 'bold',
                                            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                        }}>
                                            Paid Cash: {formatCurrency(reportData.totalPaidCash || 0)}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: logoColors.secondary,
                                            fontWeight: 'bold',
                                            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                        }}>
                                            Paid Online: {formatCurrency(reportData.totalPaidOnline || 0)}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: (reportData.totalOutstandingDues || 0) > 0 ? '#ff9800' : logoColors.secondary,
                                            fontWeight: 'bold',
                                            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                        }}>
                                            Outstanding Dues: {formatCurrency(reportData.totalOutstandingDues || 0)}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card sx={{ 
                                backgroundColor: logoColors.primary + '20',
                                border: `2px solid ${logoColors.primary}`,
                                borderRadius: 2
                            }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ 
                                        color: logoColors.secondary, 
                                        mb: 2, 
                                        fontWeight: 'bold',
                                        textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                    }}>
                                        Report Period
                                    </Typography>
                                    <Typography variant="body1" sx={{ 
                                        fontWeight: 'bold',
                                        color: logoColors.secondary,
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                    }}>
                                        {reportData.startDate} to {reportData.endDate}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                        color: logoColors.secondary, 
                                        mt: 1,
                                        fontWeight: 'bold',
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                    }}>
                                        {reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Court Analysis */}
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ 
                                p: 2, 
                                backgroundColor: logoColors.primary + '20',
                                border: `2px solid ${logoColors.primary}`,
                                borderRadius: 2
                            }}>
                                    <Typography variant="h6" sx={{ 
                                        color: logoColors.secondary, 
                                        mb: 2,
                                        fontWeight: 'bold',
                                        textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                    }}>
                                        Court Analysis
                                    </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ 
                                                    color: logoColors.secondary, 
                                                    fontWeight: 'bold',
                                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                }}>Court</TableCell>
                                                <TableCell align="right" sx={{ 
                                                    color: logoColors.secondary, 
                                                    fontWeight: 'bold',
                                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                }}>Bookings</TableCell>
                                                <TableCell align="right" sx={{ 
                                                    color: logoColors.secondary, 
                                                    fontWeight: 'bold',
                                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                }}>Revenue</TableCell>
                                                <TableCell align="right" sx={{ 
                                                    color: logoColors.secondary, 
                                                    fontWeight: 'bold',
                                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                }}>Pending</TableCell>
                                                <TableCell align="right" sx={{ 
                                                    color: logoColors.secondary, 
                                                    fontWeight: 'bold',
                                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                }}>Cancelled</TableCell>
                                                <TableCell align="right" sx={{
                                                    color: logoColors.secondary,
                                                    fontWeight: 'bold',
                                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                }}>Conflict</TableCell>
                                                <TableCell align="right" sx={{
                                                    color: logoColors.secondary,
                                                    fontWeight: 'bold',
                                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                }}>Paid Cash</TableCell>
                                                <TableCell align="right" sx={{
                                                    color: logoColors.secondary,
                                                    fontWeight: 'bold',
                                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                }}>Paid Online</TableCell>
                                                <TableCell align="right" sx={{
                                                    color: logoColors.secondary,
                                                    fontWeight: 'bold',
                                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                }}>Dues</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {reportData.courtReports?.map((court) => (
                                                <TableRow key={court.courtNumber}>
                                                    <TableCell sx={{ 
                                                        color: logoColors.secondary,
                                                        fontWeight: 'bold',
                                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                    }}>Court {court.courtNumber}</TableCell>
                                                    <TableCell align="right" sx={{ 
                                                        color: logoColors.secondary,
                                                        fontWeight: 'bold',
                                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                    }}>{court.bookingCount}</TableCell>
                                                    <TableCell align="right" sx={{ 
                                                        color: logoColors.secondary,
                                                        fontWeight: 'bold',
                                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                    }}>{formatCurrency(court.revenue)}</TableCell>
                                                    <TableCell align="right" sx={{ 
                                                        color: logoColors.secondary,
                                                        fontWeight: 'bold',
                                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                    }}>
                                                        {court.pendingAmount > 0 ? formatCurrency(court.pendingAmount) : '-'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ 
                                                        color: logoColors.secondary,
                                                        fontWeight: 'bold',
                                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                    }}>
                                                        {court.cancelledAmount > 0 ? formatCurrency(court.cancelledAmount) : '-'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{
                                                        color: logoColors.secondary,
                                                        fontWeight: 'bold',
                                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                    }}>
                                                        {court.conflictAmount > 0 ? formatCurrency(court.conflictAmount) : '-'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{
                                                        color: logoColors.secondary,
                                                        fontWeight: 'bold',
                                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                    }}>
                                                        {formatCurrency(court.paidCash || 0)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{
                                                        color: logoColors.secondary,
                                                        fontWeight: 'bold',
                                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                    }}>
                                                        {formatCurrency(court.paidOnline || 0)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{
                                                        color: (court.outstandingDues || 0) > 0 ? '#ff9800' : logoColors.secondary,
                                                        fontWeight: 'bold',
                                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                                    }}>
                                                        {formatCurrency(court.outstandingDues || 0)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>

                        {/* Detailed Bookings - one row per booking, same layout as the Excel export */}
                        <Grid item xs={12}>
                            <Paper sx={{
                                p: 2,
                                backgroundColor: logoColors.primary + '20',
                                border: `2px solid ${logoColors.primary}`,
                                borderRadius: 2
                            }}>
                                <Typography variant="h6" sx={{
                                    color: logoColors.secondary,
                                    mb: 2,
                                    fontWeight: 'bold',
                                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                }}>
                                    Detailed Bookings ({reportData.bookings?.length || 0})
                                </Typography>
                                <TableContainer sx={{ maxHeight: 500, overflowY: 'auto' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                {['Date', 'Time', 'Booking Name', 'Opponent', 'Contact', 'Charges', 'Paid Cash', 'Paid Online', 'Balance', 'Status', 'Remarks'].map((label, i) => (
                                                    <TableCell key={label} align={i >= 5 && i <= 8 ? 'right' : 'left'} sx={{
                                                        color: logoColors.secondary,
                                                        fontWeight: 'bold',
                                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                                        backgroundColor: logoColors.primary
                                                    }}>{label}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {reportData.bookings?.map((booking) => {
                                                const balance = booking.amountDue || 0;
                                                return (
                                                    <TableRow key={booking.id}>
                                                        <TableCell sx={{ color: logoColors.secondary, fontWeight: 'bold' }}>
                                                            {booking.bookingDate}
                                                        </TableCell>
                                                        <TableCell sx={{ color: logoColors.secondary, fontWeight: 'bold' }}>
                                                            {booking.timeRange}
                                                        </TableCell>
                                                        <TableCell sx={{ color: logoColors.secondary, fontWeight: 'bold' }}>
                                                            {booking.customerName}
                                                        </TableCell>
                                                        <TableCell sx={{ color: logoColors.secondary, fontWeight: 'bold' }}>
                                                            {booking.opponentName || '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: logoColors.secondary, fontWeight: 'bold' }}>
                                                            {booking.customerPhone}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ color: logoColors.secondary, fontWeight: 'bold' }}>
                                                            {formatCurrency(booking.totalAmount)}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ color: logoColors.secondary, fontWeight: 'bold' }}>
                                                            {formatCurrency(booking.paidCashAmount || 0)}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ color: logoColors.secondary, fontWeight: 'bold' }}>
                                                            {formatCurrency(booking.paidOnlineAmount || 0)}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{
                                                            color: balance > 0 ? '#ff9800' : logoColors.secondary,
                                                            fontWeight: 'bold'
                                                        }}>
                                                            {formatCurrency(balance)}
                                                        </TableCell>
                                                        <TableCell sx={{ color: logoColors.secondary, fontWeight: 'bold' }}>
                                                            {balance <= 0 ? 'Paid' : 'Pending'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: logoColors.secondary, fontWeight: 'bold' }}>
                                                            {booking.remarks || '-'}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                    </Grid>
                )}

            </Box>
        </LocalizationProvider>
    );
};

export default ReportsPage;